/**
 * NovaSpinner — drop-in replacement for `<Loader2 className="animate-spin" />`.
 *
 * Same size + className API so the stabilization sweep is mechanical:
 * change the import + tag name, leave everything else.
 *
 * NOVA-LOAD-1 — brought onto the 2026 kit.
 *
 * WHY it isn't a whole Nova: this thing renders at 16px inside buttons,
 * table cells and status pills. A full-body kit avatar at that scale is
 * mush. So the spinner stays what it always was — a zoomed-in crop of
 * Nova's antenna: the orbital ring, spinning at the canonical 1.5s, with
 * the ball at its center. What changed is the ball. It now cycles the
 * same ROYGBV rainbow on the same 3s loop as `OrbitLoader`'s eye-block
 * build, via the shared `.nova-rainbow-ball` rule in nova.css, so an
 * inline spinner and a full-page loader are visibly the same animal.
 *
 * The ring deliberately keeps `--lunar-teal` rather than the kit's ivory:
 * these render on both light and dark button fills, and ivory-on-cream
 * would vanish. Contrast beats palette purity at 16px.
 */

import { cn } from '@/lib/utils';
import { NOVA_RAINBOW } from '@/components/nova/kit';
// Supplies `.nova-rainbow-ball`. Cheap — the loader pulls the same
// stylesheet in at the app root anyway, so this is a no-op at runtime.
import '@/components/nova/kit/nova.css';

interface NovaSpinnerProps {
  /** Pixel size. Defaults to 16 (matches lucide-react default). */
  size?: number;
  /** Extra classes for the wrapper SVG. */
  className?: string;
  /** Accessible label. Defaults to "Loading". */
  ariaLabel?: string;
}

/// Ring color as a CSS-var string so the spinner inherits the active
/// theme — including any custom-domain branding overrides.
const TEAL = 'hsl(var(--lunar-teal))';

export const NovaSpinner = ({
  size = 16,
  className,
  ariaLabel = 'Loading',
}: NovaSpinnerProps) => {
  return (
    <svg
      className={cn('inline-block', className)}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      role="status"
      aria-label={ariaLabel}
    >
      {/* Antenna ring — teal, 1.5s spin, same tempo as the kit's
          `.expr-thinking .antenna-spin`. */}
      <g className="nova-spinner-ring">
        <circle
          cx="12"
          cy="12"
          r="10"
          fill="none"
          stroke={TEAL}
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="40 22"
          opacity="0.7"
        />
      </g>
      {/* Antenna ball — rainbow cycle, driven by nova.css. */}
      <circle cx="12" cy="12" r="3" className="nova-rainbow-ball" fill={NOVA_RAINBOW[0]} />
      {/* Ring rotation stays inline so the spinner still animates in
          isolated preview contexts that don't load nova.css. */}
      <style>{`
        @keyframes nova-spinner-ring {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .nova-spinner-ring {
          transform-origin: center;
          animation: nova-spinner-ring 1.5s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .nova-spinner-ring { animation: none; }
        }
      `}</style>
    </svg>
  );
};
